import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, X } from 'lucide-react';

const FORM_URL = 'https://api.leadconnectorhq.com/widget/form/KkXYIE5MeSJOUUHG0Mou';

// Colores finales
const NAVY_BODY = '#0B1633';
const NAVY_GRAD_END = '#0A1530';
const NAVY_HEADER = '#1A2C5B';
const NAVY_DIVIDER = '#0D1A39';
const BLUE_HOVER = '#1F44B3';

const FinalCTA = () => {
  const [open, setOpen] = useState(false);
  const openModal = useCallback(() => setOpen(true), []);
  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [close]);

  return (
    <div className="relative mb-12 pb-[12vh]">
      {/* Fondo video */}
      <div className="absolute inset-x-0 -bottom-20 h-[60vh] z-0 overflow-hidden pointer-events-none">
        <video
          className="w-full h-full object-cover object-center"
          autoPlay loop muted playsInline
          src="https://res.cloudinary.com/dv1ald7mk/video/upload/v1700000000/ROCKET_FULL_aeetri.mp4"
        />
      </div>

      {/* CTA */}
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.5 }}
        className="relative bg-gray-900/50 backdrop-blur-lg rounded-3xl p-4 md:p-12 shadow-2xl border border-gray-700/50 text-center z-10 max-w-[90vw] mx-auto"
      >
        <h2 className="text-xl md:text-4xl font-extrabold mb-2 md:mb-4 text-white">
          Ready To Launch Your Credit?
        </h2>
        <p className="text-sm md:text-xl text-gray-300 mb-4 md:mb-8 max-w-3xl mx-auto">
          Join thousands who have already launched their financial future. Your countdown to success starts now.
        </p>
        <motion.button
          onClick={openModal}
          whileHover={{ scale: 1.05, boxShadow: '0 0 15px rgba(0,255,255,.7), 0 0 30px rgba(0,255,255,.5)' }}
          whileTap={{ scale: 0.95 }}
          className="px-4 py-2 text-sm md:px-8 md:py-4 md:text-lg bg-[#00ffcc] rounded-full font-bold text-black shadow-lg flex items-center justify-center gap-2 mx-auto neon-turquoise"
          aria-haspopup="dialog" aria-controls="lead-form-modal"
        >
          <span>Begin Your Launch</span>
          <ArrowRight size={16} className="md:size-20" />
        </motion.button>
      </motion.div>

      {/* MODAL */}
      {open && (
        <div className="fixed inset-0 z-[100]">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/60" onClick={close} />

          <div className="relative h-full w-full flex items-center justify-center p-4">
            <motion.div
              id="lead-form-modal" role="dialog" aria-modal="true"
              initial={{ opacity: 0, y: 12, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.22 }}
              className="w-[92vw] max-w-[720px] rounded-2xl shadow-2xl overflow-hidden"
              style={{ background: `linear-gradient(180deg, ${NAVY_BODY} 0%, ${NAVY_GRAD_END} 100%)` }}
            >
              {/* Header */}
              <div className="relative px-6 md:px-8 py-5" style={{ backgroundColor: NAVY_HEADER }}>
                <h3
                  className="text-white text-center font-bold tracking-wide"  // <- negrita
                  style={{
                    fontFamily: `'Inter', ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, 'Apple Color Emoji', 'Segoe UI Emoji'`,
                    fontSize: '18px',
                    lineHeight: '28px',
                    fontWeight: 700, // <- negrita explícita
                  }}
                >
                  Schedule a Consultation
                </h3>

                {/* Botón X con círculo azul */}
                <button
                  onClick={close}
                  aria-label="Close"
                  className="absolute right-4 top-1/2 -translate-y-1/2 inline-flex items-center justify-center"
                  style={{
                    width: 36, height: 36, borderRadius: '9999px',
                    backgroundColor: 'transparent', transition: 'background-color .15s ease'
                  }}
                  onMouseEnter={e => (e.currentTarget.style.backgroundColor = BLUE_HOVER)}
                  onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                  onMouseDown={e => (e.currentTarget.style.backgroundColor = BLUE_HOVER)}
                  onMouseUp={e => (e.currentTarget.style.backgroundColor = BLUE_HOVER)}
                >
                  <X className="h-5 w-5 text-white" />
                </button>

                {/* Franja separadora inferior */}
                <div className="absolute left-0 right-0 -bottom-[1px]" style={{ height: 6, backgroundColor: NAVY_DIVIDER }} />
              </div>

              {/* Panel blanco interno */}
              <div className="p-5">
                <div className="bg-white rounded-xl overflow-hidden">
                  <iframe
                    title="Schedule a Consultation"
                    src={FORM_URL}
                    className="w-full"
                    style={{ height: 520, border: 0 }}
                    loading="lazy"
                    allow="clipboard-write; autoplay"
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      )}

      <style jsx>{`
        .neon-turquoise {
          background-color: #00ffcc;
          box-shadow: 0 0 10px rgba(0,255,204,.7), 0 0 20px rgba(0,255,204,.5);
          transition: all .3s ease;
        }
        .neon-turquoise:hover {
          box-shadow: 0 0 15px rgba(0,255,204,.9), 0 0 30px rgba(0,255,204,.7), 0 0 45px rgba(0,255,204,.5);
        }
      `}</style>
    </div>
  );
};

export default FinalCTA;